// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { addProductCustomerPartNumber, deleteProductCustomerPartNumber, updateProductCustomerPartNumber } from "@steiner/App/Services/ProductService";

interface Props {
    result: {
        errorMessage?: string;
    };
}

export interface UpdateCustomerPartNumberParameter {
    customerPartNumber: string;
    productNumber: string;
    type: "add" | "edit" | "delete";
}

type HandlerType = Handler<UpdateCustomerPartNumberParameter & HasOnSuccess & HasOnError<string>, Props>;

export const RequestDataFromApi: HandlerType = async props => {
    props.result = {};
    try {
        switch(props.parameter.type) {
            case "add":
                await addProductCustomerPartNumber({
                    customerPartNumber: props.parameter.customerPartNumber,
                    productNumber: props.parameter.productNumber,
                });
            break;
            case "edit":
                await updateProductCustomerPartNumber({
                    customerPartNumber: props.parameter.customerPartNumber,
                    productNumber: props.parameter.productNumber,
                });
            break;
            case "delete":
                await deleteProductCustomerPartNumber({
                    customerPartNumber: props.parameter.customerPartNumber,
                    productNumber: props.parameter.productNumber,
                });
            break;
        }
        
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        throw error;
    }
};

export const DispatchUpdateCustomerPartNumber: HandlerType = props => {
    if (!props.result.errorMessage) {
        props.dispatch({
            type: "xCustomerPartNumbers/updateCustomerPartNumber",
            customerPartNumber: props.parameter.customerPartNumber,
            productNumber: props.parameter.productNumber,
        } as any);
    }
};

/**
 * TODO: Investigation
 * Classic site calls postProduct service after any update (Add, Edit or Delete) to the customer part number
 * it looks like FE doesn't rely on this service
 * but need to check if BE needs this or not anymore
 * for now we agreed to get rid of it
 */

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.result?.errorMessage && props.parameter.onSuccess) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess();
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.result?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.result.errorMessage);
    }
};

export const chain = [
    RequestDataFromApi,
    DispatchUpdateCustomerPartNumber,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const updateProductCutsomerPartNumber = createHandlerChainRunner(chain, "updateProductCutsomerPartNumber");

export default updateProductCutsomerPartNumber;
